import React from 'react';

import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { TelepointerAvatar } from '../../ui/components/TelepointerAvatar/TelepointerAvatar';
import { resolveTelepointerPresentation } from '../../ui/components/TelepointerAvatar/telepointerPresentation';
import type { AgentInfo } from '../decoration/reducer';

import { TELEPOINTER_AVATAR_PORTAL_KEY } from './decorations';
import type { TelepointerElements } from './decorations';

type GetTelepointerLabel = (options: {
	agentName?: string;
	isAgentMentionEnabled: boolean;
}) => string;

const isAgentMentionsEnabled = (): boolean =>
	expValEquals('platform_editor_agent_mentions', 'isEnabled', true);

const isTelepointerRedesignEnabled = (): boolean =>
	isAgentMentionsEnabled() && isExperimentEnabled('platform_editor_agent_telepointer_redesign');

/**
 * Keeps the frontend streaming telepointer's badge in step with the current agent, reusing the
 * consolidated `agent-edit-chrome` plugin's presentation resolver and `TelepointerAvatar`
 * component so both telepointers render the same redesign (Rovo / agent / fallback pill, with the
 * badge, cursor line and content highlight sharing one hue).
 *
 * Unlike the backend-streamed path, the frontend path holds the agent identity locally in the
 * streaming session's `agentInfo`, so its avatar resolves without a per-step `agentId`. When the
 * redesign is off, or the session is not agent-attributed (plain AI streaming carries no
 * `agentInfo`), the plain localised streaming label is shown instead.
 *
 * Returns a stateful updater for the plugin's `view.update()`. It memoises the last treatment so
 * the hot streaming path avoids redundant DOM writes and portal re-renders, plus a `destroy` to
 * tear down the portal with the plugin view.
 */
export const createStreamingTelepointerBadgeUpdater = ({
	elements: { avatarContainer, element, labelElement },
	getTelepointerLabel,
	portalProviderAPI,
}: {
	elements: TelepointerElements;
	getTelepointerLabel: GetTelepointerLabel;
	portalProviderAPI: PortalProviderAPI;
}): { destroy: () => void; update: (agentInfo?: AgentInfo) => void } => {
	// null = a badge was rendered then removed; undefined = never rendered.
	let lastRenderedAgentKey: string | null = null;
	// Guards the label/avatar display swap so it isn't rewritten on every streaming transaction.
	let showingBadge: boolean | undefined;
	let lastLineColor = '';

	const update = (agentInfo?: AgentInfo): void => {
		// The resolver always returns a kind (rovo / agent / fallback), so a presentation means a
		// badge; the redesign gate and an agent-attributed session are the only preconditions.
		const presentation =
			isTelepointerRedesignEnabled() && agentInfo
				? resolveTelepointerPresentation(agentInfo)
				: undefined;

		if (presentation && agentInfo) {
			if (showingBadge !== true) {
				showingBadge = true;
				labelElement.style.display = 'none';
				avatarContainer.style.display = '';
				// The redesign cursor line is 2px and the height of the content highlight. Gated with
				// the colour so the off-experiment and agent-edit-chrome lines keep the 1px / 24px
				// default in the shared CSS.
				element.style.setProperty('--agent-edit-chrome-telepointer-line-width', '2px');
				element.style.setProperty('--agent-edit-chrome-telepointer-line-height', '20px');
			}

			// The badge fill and cursor line take the agent's hue from this variable, read by the
			// shared GlobalWrapper CSS — the same pairing the consolidated plugin sets.
			const lineColor = presentation.telepointerLineColor ?? '';
			if (lastLineColor !== lineColor) {
				lastLineColor = lineColor;
				if (lineColor) {
					element.style.setProperty('--agent-edit-chrome-telepointer-line-color', lineColor);
				} else {
					element.style.removeProperty('--agent-edit-chrome-telepointer-line-color');
				}
			}

			const { agentId, agentIdentityAccountId, agentName, agentNamedId } = agentInfo;
			const kind = presentation.telepointerAgentKind;
			// Re-render on any change to a badge input, not just the identity account id.
			const agentRenderKey = [kind, agentNamedId, agentIdentityAccountId, agentId, agentName].join(
				'|',
			);
			if (lastRenderedAgentKey !== agentRenderKey) {
				lastRenderedAgentKey = agentRenderKey;
				portalProviderAPI.render(
					() =>
						React.createElement(TelepointerAvatar, {
							agentId,
							agentIdentityAccountId,
							agentName,
							agentNamedId: agentNamedId ?? undefined,
							kind,
						}),
					avatarContainer,
					TELEPOINTER_AVATAR_PORTAL_KEY,
				);
			}
			return;
		}

		if (showingBadge !== false) {
			showingBadge = false;
			avatarContainer.style.display = 'none';
			labelElement.style.display = '';
			element.style.removeProperty('--agent-edit-chrome-telepointer-line-width');
			element.style.removeProperty('--agent-edit-chrome-telepointer-line-height');
		}
		if (lastLineColor !== '') {
			lastLineColor = '';
			element.style.removeProperty('--agent-edit-chrome-telepointer-line-color');
		}
		if (lastRenderedAgentKey !== null) {
			lastRenderedAgentKey = null;
			portalProviderAPI.remove(TELEPOINTER_AVATAR_PORTAL_KEY);
		}
		const newLabel = getTelepointerLabel({
			agentName: agentInfo?.agentName,
			isAgentMentionEnabled: isAgentMentionsEnabled(),
		});
		if (labelElement.textContent !== newLabel) {
			labelElement.textContent = newLabel;
		}
	};

	const destroy = (): void => {
		if (lastRenderedAgentKey !== null) {
			lastRenderedAgentKey = null;
			portalProviderAPI.remove(TELEPOINTER_AVATAR_PORTAL_KEY);
		}
	};

	return { destroy, update };
};
